import { useCallback, useMemo, useState } from 'react'

import {
  FractionSimplifyDenomQuestionModel,
  getFractionSimplifyDenomQuestion as gFracQues,
} from './FractionSimplifyDenom.utils'

interface AnswerModel {
  leftNumFactor?: string
  leftDenomFactor?: string
  rightNum?: string
}

interface ErrorModel {
  leftNumFactor?: boolean
  leftDenomFactor?: boolean
  rightNum?: boolean
}

interface ReturnType {
  question: FractionSimplifyDenomQuestionModel
  formValid: boolean
  answer: AnswerModel
  error?: ErrorModel
  handleCheckAnswer: () => void
  onInputChange: (input?: InputTypes, val?: string) => void
  goToNextQuestion: () => void
}

interface UseProperFractionProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

type InputTypes = 'leftNumFactor' | 'leftDenomFactor' | 'rightNum'

function useFractionSimplifyDenom({
  difficultyLevel,
  onAnswerStateChange,
  onNextQuestionClick,
}: UseProperFractionProps): ReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [answer, setAnswer] = useState<AnswerModel>({})
  const [error, setError] = useState<ErrorModel>()
  const question = useMemo(() => gFracQues(difficultyLevel, taskHistory), [difficultyLevel, taskHistory])

  const handleCheckAnswer = useCallback((): void => {
    const { leftNumFactor = '0', leftDenomFactor = '0', rightNum = '0' } = answer
    const { divisor, leftNum } = question
    const err: ErrorModel = {
      leftNumFactor: divisor !== Number(leftNumFactor),
      leftDenomFactor: divisor !== Number(leftDenomFactor),
      rightNum: Number(rightNum) !== leftNum / divisor,
    }
    onAnswerStateChange(!(err.leftNumFactor || err.leftDenomFactor || err.rightNum))
    setError(err)
  }, [question, answer, onAnswerStateChange, setError])

  const onInputChange = useCallback(
    (input?: InputTypes, val?: string) => {
      switch (input) {
        case 'leftNumFactor':
          setAnswer({ ...answer, leftNumFactor: val })
          break
        case 'leftDenomFactor':
          setAnswer({ ...answer, leftDenomFactor: val })
          break
        case 'rightNum':
          setAnswer({ ...answer, rightNum: val })
          break
        default:
          setAnswer({})
      }
    },
    [answer],
  )

  const goToNextQuestion = useCallback(() => {
    const { leftDenom, leftNum, divisor } = question
    const newTaskHistory = [`${divisor}-${leftNum}-${leftDenom}`, ...taskHistory].slice(0, question.maxVariation)
    setTaskHistory(newTaskHistory)
    onInputChange()
    onNextQuestionClick()
    setError(undefined)
  }, [taskHistory, onInputChange, onNextQuestionClick, question])

  return {
    answer,
    formValid: !!answer.leftNumFactor && !!answer.leftDenomFactor && !!answer.rightNum,
    question,
    error,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
  } as const
}

export default useFractionSimplifyDenom
